import { useI18n } from '../../../i18n';
import { Pagination } from '../../../components/shared/Pagination';
import { EditIcon, TrashIcon } from '../../../components/icons';
import { SupplierStatusBadge, type SupplierStatus } from './SupplierStatusBadge';

export interface SupplierDisplay {
  id: string;
  code: string;
  name: string;
  category?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  taxCode?: string;
  address?: string;
  paymentMethod?: string;
  paymentTerms?: string;
  deliveryTime?: string;
  status: SupplierStatus;
}

interface SupplierTableProps {
  data: SupplierDisplay[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onView: (supplierId: string) => void;
  onEdit: (supplierId: string) => void;
  onDelete: (supplierId: string) => void;
  isLoading?: boolean;
}

function getPaymentTermsLabel(method?: string, terms?: string): string {
  if (terms) return terms;
  const map: Record<string, string> = {
    wire_transfer: 'Wire Transfer',
    credit_card: 'Corporate Credit',
    invoice: 'Invoice',
    credit_note: 'Credit Note',
    refund_request: 'Refund Request',
  };
  return map[method || ''] || method || '-';
}

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 7 }).map((_, i) => (
        <tr key={i} className="border-b border-slate-50 animate-pulse">
          {[140, 140, 160, 100, 80, 80, 80].map((w, j) => (
            <td key={j} className="px-4 py-4">
              <div className={`h-4 bg-slate-100 rounded`} style={{ width: w }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function SupplierTable({
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  isLoading,
}: SupplierTableProps) {
  const { t } = useI18n();
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              {[
                t('supplierName'),
                t('contactName'),
                t('phone'),
                t('taxCode'),
                t('avgDeliveryTime'),
                t('status'),
                t('action'),
              ].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          {isLoading ? (
            <TableSkeleton />
          ) : data.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7} className="py-16 text-center text-sm text-slate-400">{t('noSuppliersFound')}</td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {data.map((row) => (
                <tr key={row.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  {/* Supply Name */}
                  <td className="px-4 py-4">
                    <span className="text-sm font-semibold text-slate-800">{row.name}</span>
                  </td>

                  {/* Contact.
                      Bug #187 (reviewer bắt): cột có header "Contact Name" nhưng
                      trước đây render... SỐ ĐIỆN THOẠI — trùng nguyên xi với cột
                      Phone bên cạnh, còn `contactName` thì không hiện ở đâu cả.
                      Tìm theo tên người liên hệ ra dòng mà trên màn hình không
                      thấy chữ nào khớp, trông y như bộ lọc hỏng. */}
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{row.contactName || '-'}</p>
                      {row.email && <p className="text-xs text-slate-400">{row.email}</p>}
                    </div>
                  </td>

                  {/* Phone */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-slate-600">{row.phone || '-'}</span>
                  </td>

                  {/* Tax Code */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-slate-600">{row.taxCode || '-'}</span>
                  </td>

                  {/* Delivery Time */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-slate-600">{row.deliveryTime || '-'}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4">
                    <SupplierStatusBadge status={row.status} />
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit(row.id)}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-primary-600 transition-colors"
                        title="Edit"
                      >
                        <EditIcon size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(row.id)}
                        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {totalCount > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="suppliers"
          />
        </div>
      )}
    </div>
  );
}
